/**
 * Недельно-суточный план (ПЛ-35): работы на 3–6 недель вперёд, план недели, подтверждение обязательств
 * бригадиром, перепланирование с причиной, итог недели с причинами и процент выполненных обязательств;
 * сменные задания прорабам на день смены с объёмом на день (ПЛ-28).
 */
import {
  COMMITMENT_STATUSES,
  type CommitmentRow,
  MISS_REASONS,
  type MissReason,
  type PpcReport,
  type ScheduleView,
  type WeekPlan,
} from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { useMemo, useState } from 'react';
import { api } from '../../lib/api';
import { Button } from '../../ui/button';
import { Badge, EmptyState, ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Input, Select } from '../../ui/form';
import { Panel } from '../../ui/layout';
import { LField } from './LField';
import { ShiftTasksBlock } from './ShiftTasksBlock';

const dd = (s: string) => s.slice(0, 10).split('-').reverse().join('.');
const shift = (d: string, k: number) => {
  const t = new Date(`${d}T00:00:00Z`);
  t.setUTCDate(t.getUTCDate() + k);
  return t.toISOString().slice(0, 10);
};
const TONE: Record<CommitmentRow['status'], 'neutral' | 'warning' | 'success' | 'danger'> = {
  planned: 'neutral',
  committed: 'warning',
  done: 'success',
  missed: 'danger',
};

export function WeekPlanPanel({ view }: { view: ScheduleView }) {
  const id = view.schedule.id;
  const qc = useQueryClient();
  const [week, setWeek] = useState<string | null>(null);
  const [weeks, setWeeks] = useState(4);
  const [zone, setZone] = useState('');
  const [pick, setPick] = useState<Set<string>>(new Set());
  const q = useQuery({
    queryKey: ['week-plan', id, week, weeks],
    queryFn: () => api<WeekPlan>(`/schedules/${id}/week-plan`, { query: { week: week ?? undefined, weeks } }),
  });
  const ppc = useQuery({
    queryKey: ['ppc', id],
    queryFn: () => api<PpcReport>(`/schedules/${id}/ppc`, { query: { weeks: 12 } }),
  });
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['week-plan', id] });
    void qc.invalidateQueries({ queryKey: ['ppc', id] });
  };
  const err = (e: Error) => toast(e.message, 'danger');
  const add = useMutation({
    mutationFn: () =>
      api(`/schedules/${id}/commitments`, {
        body: { week: q.data!.week, items: [...pick].map((activityId) => ({ activityId })) },
      }),
    onSuccess: () => {
      setPick(new Set());
      toast('Работы поставлены в план недели');
      refresh();
    },
    onError: err,
  });
  const act = useMutation({
    mutationFn: (x: { url: string; method?: 'POST' | 'PATCH' | 'DELETE'; body?: unknown }) =>
      api(x.url, { method: x.method ?? 'POST', body: x.body }),
    onSuccess: refresh,
    onError: err,
  });
  const d = q.data;
  const planned = useMemo(() => new Set((d?.commitments ?? []).map((c) => c.activityId)), [d]);
  const zoneName = new Map((d?.zones ?? []).map((z) => [z.id, z.name]));
  const rows = (d?.lookahead ?? []).filter((a) => !zone || a.zoneId === zone);
  const weekEnd = d ? shift(d.week, 6) : '';
  const thisWeek = (a: { es: string; ef: string }) =>
    d && a.es.slice(0, 10) <= weekEnd && a.ef.slice(0, 10) >= d.week;
  return (
    <div className="flex flex-col gap-4">
      <Panel title="Недельно-суточный план" meta="обязательства бригад на неделю">
        <div className="flex flex-wrap items-end gap-2">
          <Button
            onClick={() => {
              if (d) setWeek(shift(d.week, -7));
              setPick(new Set());
            }}
            className="max-md:h-11"
            aria-label="Предыдущая неделя"
          >
            ←
          </Button>
          <span className="min-h-11 content-center px-1 text-[14px] font-medium md:min-h-0">
            {d ? `Неделя ${dd(d.week)} — ${dd(weekEnd)}` : '…'}
          </span>
          <Button
            onClick={() => {
              if (d) setWeek(shift(d.week, 7));
              setPick(new Set());
            }}
            className="max-md:h-11"
            aria-label="Следующая неделя"
          >
            →
          </Button>
          <LField label="Смотреть вперёд">
            <Select
              value={String(weeks)}
              onChange={(e) => {
                setWeeks(Number(e.target.value));
                setPick(new Set());
              }}
              className="max-md:h-11"
            >
              {[3, 4, 5, 6].map((n) => (
                <option key={n} value={n}>
                  {n} недели
                </option>
              ))}
            </Select>
          </LField>
          {d?.zones.length ? (
            <LField label="Участок">
              <Select
                value={zone}
                onChange={(e) => {
                  setZone(e.target.value);
                  setPick(new Set());
                }}
                className="max-md:h-11"
              >
                <option value="">Все</option>
                {d.zones.map((z) => (
                  <option key={z.id} value={z.id}>
                    {z.name}
                  </option>
                ))}
              </Select>
            </LField>
          ) : null}
        </div>
      </Panel>
      {q.isPending ? <Spinner /> : null}
      {q.isError ? <ErrorBox error={q.error} /> : null}
      {d ? (
        <>
          <Panel title="Обязательства недели" meta={`${d.commitments.length}`}>
            {d.commitments.length ? (
              <ul className="divide-y divide-line text-[14px]">
                {d.commitments
                  .filter((c) => !zone || c.zoneId === zone)
                  .map((c) => (
                    <CommitmentItem
                      key={c.id}
                      c={c}
                      canPlan={d.canPlan}
                      act={(x) => act.mutate(x)}
                      busy={act.isPending}
                    />
                  ))}
              </ul>
            ) : (
              <p className="text-[14px] text-ink-2">
                На эту неделю обязательств нет. Отметьте работы ниже и поставьте их в план.
              </p>
            )}
          </Panel>
          <ShiftTasksBlock
            scheduleId={id}
            projectId={view.schedule.projectId}
            week={d.week}
            commitments={d.commitments.filter((c) => !zone || c.zoneId === zone)}
            people={view.people}
          />
          <Panel
            title={`Работы на ${d.weeks} недели вперёд`}
            actions={
              d.canPlan ? (
                <Button
                  variant="primary"
                  disabled={!pick.size}
                  loading={add.isPending}
                  onClick={() => add.mutate()}
                  className="max-md:h-11"
                >
                  В план недели{pick.size ? ` · ${pick.size}` : ''}
                </Button>
              ) : null
            }
          >
            {rows.length ? (
              <ul className="divide-y divide-line text-[14px]">
                {rows.map((a) => (
                  <li key={a.id} className="flex flex-wrap items-center justify-between gap-2 py-1.5">
                    <label className="flex min-h-11 min-w-0 items-center gap-2 md:min-h-0">
                      <input
                        type="checkbox"
                        disabled={!d.canPlan || planned.has(a.id)}
                        checked={planned.has(a.id) || pick.has(a.id)}
                        onChange={(e) => {
                          const n = new Set(pick);
                          if (e.target.checked) n.add(a.id);
                          else n.delete(a.id);
                          setPick(n);
                        }}
                      />
                      <span className={clsx('min-w-0', a.critical && 'text-danger')}>
                        {a.code} {a.name}
                        {a.zoneId ? <span className="text-ink-2"> · {zoneName.get(a.zoneId)}</span> : null}
                      </span>
                    </label>
                    <span className="num text-[12px] text-ink-2">
                      {dd(a.es)} — {dd(a.ef)}
                      {a.started ? ` · ${a.percent} %` : ''}
                      {thisWeek(a) ? <Badge className="ml-2">эта неделя</Badge> : null}
                      {planned.has(a.id) ? (
                        <Badge tone="success" className="ml-1">
                          в плане
                        </Badge>
                      ) : null}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <EmptyState title="Работ на эти недели нет">Измените неделю или участок.</EmptyState>
            )}
          </Panel>
        </>
      ) : null}
      {ppc.data && ppc.data.weeks.length ? <PpcBlock r={ppc.data} /> : null}
    </div>
  );
}

function CommitmentItem({
  c,
  canPlan,
  act,
  busy,
}: {
  c: CommitmentRow;
  canPlan: boolean;
  act: (x: { url: string; method?: 'POST' | 'PATCH' | 'DELETE'; body?: unknown }) => void;
  busy: boolean;
}) {
  const [mode, setMode] = useState<'replan' | 'miss' | null>(null);
  const [start, setStart] = useState(c.planStart);
  const [finish, setFinish] = useState(c.planFinish);
  const [reason, setReason] = useState('');
  const [miss, setMiss] = useState<MissReason>('front');
  const [note, setNote] = useState('');
  const open = c.status === 'planned' || c.status === 'committed';
  return (
    <li className="flex flex-col gap-2 py-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="min-w-0">
          <span className="font-medium">
            {c.code} {c.name}
          </span>
          <span className="block text-[12px] text-ink-2">
            {dd(c.planStart)}
            {c.planFinish !== c.planStart ? ` — ${dd(c.planFinish)}` : ''}
            {c.zoneName ? ` · ${c.zoneName}` : ''}
            {c.contractorName ? ` · ${c.contractorName}` : ''}
            {c.committedByName ? ` · подтвердил ${c.committedByName}` : ''}
            {c.replans ? ` · перепланировано ${c.replans}: ${c.replanReason}` : ''}
            {c.missReason ? ` · ${MISS_REASONS[c.missReason]}${c.missNote ? `: ${c.missNote}` : ''}` : ''}
          </span>
        </span>
        <span className="flex flex-wrap items-center gap-1">
          <Badge tone={TONE[c.status]}>{COMMITMENT_STATUSES[c.status]}</Badge>
          {c.status === 'planned' && c.canCommit ? (
            <Button
              size="sm"
              disabled={busy}
              onClick={() => act({ url: `/commitments/${c.id}/commit` })}
              className="max-md:h-11"
            >
              Подтвердить
            </Button>
          ) : null}
          {c.status === 'committed' && c.canCommit ? (
            <>
              <Button
                size="sm"
                disabled={busy}
                onClick={() => act({ url: `/commitments/${c.id}/close`, body: { done: true } })}
                className="max-md:h-11"
              >
                Выполнено
              </Button>
              <Button
                size="sm"
                onClick={() => setMode(mode === 'miss' ? null : 'miss')}
                className="max-md:h-11"
              >
                Не выполнено
              </Button>
            </>
          ) : null}
          {open && canPlan ? (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                // Форма открывается с текущими датами обязательства
                setStart(c.planStart);
                setFinish(c.planFinish);
                setReason('');
                setMode(mode === 'replan' ? null : 'replan');
              }}
              className="max-md:h-11"
            >
              Даты
            </Button>
          ) : null}
          {c.status === 'planned' && canPlan ? (
            <Button
              size="sm"
              variant="ghost"
              disabled={busy}
              onClick={() => act({ url: `/commitments/${c.id}`, method: 'DELETE' })}
              className="max-md:h-11"
            >
              Убрать
            </Button>
          ) : null}
        </span>
      </div>
      {mode === 'replan' ? (
        <div className="flex flex-wrap items-end gap-2 rounded-md bg-surface-2 px-2 py-2">
          <LField label="Начало">
            <Input
              type="date"
              min={c.week}
              max={shift(c.week, 6)}
              value={start}
              onChange={(e) => setStart(e.target.value)}
              className="max-md:h-11"
            />
          </LField>
          <LField label="Окончание">
            <Input
              type="date"
              min={c.week}
              max={shift(c.week, 6)}
              value={finish}
              onChange={(e) => setFinish(e.target.value)}
              className="max-md:h-11"
            />
          </LField>
          {c.status === 'committed' ? (
            <LField label="Причина перепланирования">
              <Input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="w-64 max-w-full max-md:h-11"
              />
            </LField>
          ) : null}
          <Button
            size="sm"
            disabled={busy || (c.status === 'committed' && reason.trim().length < 3)}
            onClick={() => {
              act({
                url: `/commitments/${c.id}`,
                method: 'PATCH',
                body: {
                  planStart: start,
                  planFinish: finish,
                  ...(c.status === 'committed' ? { replanReason: reason } : {}),
                },
              });
              setMode(null);
            }}
            className="max-md:h-11"
          >
            Сохранить
          </Button>
        </div>
      ) : null}
      {mode === 'miss' ? (
        <div className="flex flex-wrap items-end gap-2 rounded-md bg-surface-2 px-2 py-2">
          <LField label="Причина">
            <Select
              value={miss}
              onChange={(e) => setMiss(e.target.value as MissReason)}
              className="max-md:h-11"
            >
              {Object.entries(MISS_REASONS).map(([k, t]) => (
                <option key={k} value={k}>
                  {t}
                </option>
              ))}
            </Select>
          </LField>
          <LField label="Пояснение">
            <Input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="w-64 max-w-full max-md:h-11"
            />
          </LField>
          <Button
            size="sm"
            disabled={busy}
            onClick={() => {
              act({
                url: `/commitments/${c.id}/close`,
                body: { done: false, missReason: miss, missNote: note || null },
              });
              setMode(null);
            }}
            className="max-md:h-11"
          >
            Закрыть неделю
          </Button>
        </div>
      ) : null}
    </li>
  );
}

function PpcBlock({ r }: { r: PpcReport }) {
  const fmt = (x: number | null) => (x == null ? '—' : `${String(x).replace('.', ',')} %`);
  return (
    <Panel title="Выполнение обязательств" meta="за последние 12 недель">
      <div
        className="mb-3 flex items-end gap-1 overflow-x-auto"
        role="img"
        aria-label="Процент выполненных обязательств по неделям"
      >
        {r.weeks.map((w) => (
          <div key={w.week} className="flex w-10 shrink-0 flex-col items-center gap-1">
            <span className="num text-[11px] text-ink-2">{w.ppc == null ? '—' : Math.round(w.ppc)}</span>
            <div className="flex h-24 w-6 items-end rounded bg-surface-2">
              <div
                className={clsx('w-6 rounded', (w.ppc ?? 0) >= 80 ? 'bg-success' : 'bg-danger')}
                style={{ height: `${w.ppc ?? 0}%` }}
              />
            </div>
            <span className="text-[10px] text-ink-2">{dd(w.week).slice(0, 5)}</span>
          </div>
        ))}
      </div>
      <div className="grid gap-4 md:grid-cols-3">
        {(
          [
            ['По подрядчикам', r.byContractor],
            ['По участкам', r.byZone],
          ] as const
        ).map(([t, list]) => (
          <div key={t}>
            <h3 className="mb-1 text-[13px] font-semibold">{t}</h3>
            <ul className="divide-y divide-line text-[13px]">
              {list.map((x) => (
                <li key={x.id || x.name} className="flex justify-between gap-2 py-1">
                  <span className="min-w-0 truncate">{x.name}</span>
                  <span className="num text-ink-2">
                    {x.done}/{x.total} · {fmt(x.ppc)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ))}
        <div>
          <h3 className="mb-1 text-[13px] font-semibold">Причины невыполнения</h3>
          {r.reasons.length ? (
            <ul className="divide-y divide-line text-[13px]">
              {r.reasons.map((x) => (
                <li key={x.reason} className="flex justify-between gap-2 py-1">
                  <span>{x.title}</span>
                  <span className="num text-ink-2">{x.count}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-[13px] text-ink-2">Невыполненных обязательств нет.</p>
          )}
        </div>
      </div>
    </Panel>
  );
}
